import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { WINDOW } from '@tvd/cdk/browser';
import { DialogService } from '@tvd/design-system/dialog';
import { EMPTY, filter, first, interval, merge, race, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UpdatesService {
  readonly #appRef = inject(ApplicationRef);
  readonly #winRef = inject(WINDOW);
  readonly #translate = inject(TranslateService);
  readonly #updates = inject(SwUpdate);
  readonly #dialog = inject(DialogService);

  readonly #isStable$ = this.#appRef.isStable.pipe(
    first((isStable) => isStable),
  );
  readonly #everySixHours$ = interval(60_000 * 60 * 6);

  readonly #checkForUpdates = race(this.#isStable$, this.#everySixHours$)
    .pipe(switchMap(() => this.#updates.checkForUpdate().catch(() => EMPTY)))
    .subscribe();

  readonly #isUpdateAvailable$ = this.#updates.versionUpdates.pipe(
    filter((update) => update.type === 'VERSION_READY'),
  );

  readonly #updateFailed$ = this.#updates.versionUpdates.pipe(
    filter((update) => update.type === 'VERSION_INSTALLATION_FAILED'),
  );

  readonly #update$ = merge(this.#isUpdateAvailable$, this.#updateFailed$)
    .pipe(
      filter(() => !this.#dialog.active),
      switchMap(() =>
        this.#dialog.confirm({
          title: this.#translate.instant('updates.title'),
          text: this.#translate.instant('updates.description'),
          confirmButtonText: this.#translate.instant('action.update'),
          rejectButtonText: this.#translate.instant('updates.later'),
        }),
      ),
      filter(Boolean),
    )
    .subscribe(() => this.#winRef.location.reload());
}
